<script lang="ts">
  import Chevron from '$icons/Chevron.svelte';
  import LoaderIcon from '$icons/loaders/LoaderIcon.svelte';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import Button from '../../Button.svelte';
  import { client } from '$lib/api/Client';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import type { ListProjectCommentsResponse } from '$shared/api';
  import type { ProjectComment } from '$shared/lib';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import { toastCustom } from '$utils/toasts';

  export let comment: ProjectComment;

  const queryClient = useQueryClient();
  const loadRepliesMutation = createMutation({
    mutationFn: async () => {
      return client.projects.listComments({
        projectId: comment.projectId,
        parentCommentId: comment.id,
        offset: comment.replies.length,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(comment.projectId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(comment.projectId));

      return { previousData };
    },
    onSuccess: (data, _, context) => {
      const { previousData } = context;
      if (previousData) {
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === comment.id) {
              return {
                ...c,
                replies: [...c.replies, ...data.comments],
                hasMoreReplies: data.hasMore,
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            comments: updateCommentInTree(page.comments),
          })),
        });
      }
    },
    onError: (error, _, context) => {
      // Roll back to previous state on error
      if (context?.previousData) {
        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), context.previousData);
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An unknown error occurred while loading more replies.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
  });

  async function loadMoreReplies() {
    await $loadRepliesMutation.mutateAsync();
  }
</script>

<Button
  size="sm"
  tertiary
  class="ml-2"
  on:click={loadMoreReplies}
  disabled={$loadRepliesMutation.isPending}
>
  {#if $loadRepliesMutation.isPending}
    <LoaderIcon class="h-4 w-4" />
  {:else}
    <Chevron />
  {/if}
  <span class="text-xs">Load more replies</span>
</Button>
